import { cn } from '@shadcn/lib/utils';
import { Card, CardContent } from '@shadcn/ui/card';
import { AlertCircle, Info, LucideIcon } from 'lucide-react';
import React from 'react';

import { Text } from '../text/text';

enum DisclaimerVariant {
  INFO = 'info',
  WARNING = 'warning',
}

interface DisclaimerConfig {
  Icon: LucideIcon;
  contentClasses: string;
  disclaimerClasses: string;
  iconClasses: string;
  textPadding: string;
}

const typeConfig: Record<DisclaimerVariant, DisclaimerConfig> = {
  [DisclaimerVariant.INFO]: {
    Icon: Info,
    contentClasses: 'bg-blue-50 border-0 shadow-none rounded-lg px-[30px]',
    disclaimerClasses: 'bg-gradient-to-r from-indigo-500 to-mauve-500 p-1 rounded-xl',
    iconClasses: 'h-9 w-9 text-indigo-500',
    textPadding: 'pl-[18px]',
  },
  [DisclaimerVariant.WARNING]: {
    Icon: AlertCircle,
    contentClasses: 'bg-yellow-200 px-[12px] border border-yellow-700 shadow-none rounded-lg',
    disclaimerClasses: 'p-0',
    iconClasses: 'h-[22px] w-[22px] text-typography',
    textPadding: 'pl-[8px]',
  },
};

function Disclaimer({
  className,
  showIcon = true,
  variant = DisclaimerVariant.INFO,
  children,
  ...props
}: IDisclaimerProps) {
  const { disclaimerClasses, contentClasses, Icon, iconClasses, textPadding } = typeConfig[variant];
  return (
    <Card className={cn(disclaimerClasses, className)} {...props}>
      <CardContent className={cn(contentClasses, ' py-3 flex items-center')}>
        {showIcon && (
          <div className="flex-shrink-0">
            <Icon className={iconClasses} data-testid={`${variant}-icon`} />
          </div>
        )}
        <Text className={cn('flex-1 leading-snug', showIcon && textPadding)} size="sm">
          {children}
        </Text>
      </CardContent>
    </Card>
  );
}

interface IDisclaimerProps extends React.ComponentPropsWithoutRef<'div'> {
  readonly showIcon?: boolean;
  readonly variant?: DisclaimerVariant;
  readonly children: React.ReactNode;
}

export { Disclaimer, DisclaimerVariant, type IDisclaimerProps };
